<template>
  <section id="resume" class="section section--alt">
    <div class="resume__inner">
      <div class="section-header">
        <p class="section-label reveal" ref="el" :class="{ 'is-visible': isVisible }">Career</p>
        <h2 class="section-title reveal reveal--d1" :class="{ 'is-visible': isVisible }">Resume & Experience</h2>
        <p class="section-desc reveal reveal--d2" :class="{ 'is-visible': isVisible }">
          A track record built across product companies and enterprise environments,
          shipping data infrastructure that delivers measurable business impact.
        </p>
      </div>

      <div class="resume__grid">
        <!-- Experience Column -->
        <div class="resume__col">
          <h3 class="resume__col-title reveal reveal--d2" :class="{ 'is-visible': isVisible }">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>
            Work Experience
          </h3>

          <div class="timeline">
            <div
              v-for="(job, idx) in experience"
              :key="job.company"
              :class="['timeline__item', 'reveal', `reveal--d${idx + 3}`, { 'is-visible': isVisible }]"
            >
              <div class="timeline__dot" :style="{ background: job.color }"></div>
              <div class="timeline__card card">
                <div class="timeline__header">
                  <div>
                    <h4 class="timeline__role">{{ job.role }}</h4>
                    <p class="timeline__company">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>
                      {{ job.company }} · {{ job.location }}
                    </p>
                  </div>
                  <div class="timeline__date">
                    <span class="tag tag--muted">{{ job.period }}</span>
                  </div>
                </div>
                <ul v-if="job.bullets.length" class="timeline__bullets">
                  <li v-for="bullet in job.bullets" :key="bullet">{{ bullet }}</li>
                </ul>
                <div class="timeline__tech">
                  <span class="tag tag--purple" v-for="tech in job.tech" :key="tech">{{ tech }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Skills & Education Column -->
        <div class="resume__col">
          <h3 class="resume__col-title reveal reveal--d2" :class="{ 'is-visible': isVisible }">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
            Key Skills
          </h3>

          <div class="skills-summary">
            <div
              v-for="(skill, idx) in keySkills"
              :key="skill.name"
              :class="['skills-summary__item', 'reveal', `reveal--d${idx + 3}`, { 'is-visible': isVisible }]"
            >
              <div class="skills-summary__header">
                <span class="skills-summary__name">{{ skill.name }}</span>
                <span class="skills-summary__pct gradient-text">{{ skill.level }}%</span>
              </div>
              <div class="skills-summary__bar-bg">
                <div
                  class="skills-summary__bar-fill"
                  :style="{ width: isVisible ? skill.level + '%' : '0%' }"
                ></div>
              </div>
            </div>
          </div>

          <h3 class="resume__col-title resume__col-title--mt reveal reveal--d3" :class="{ 'is-visible': isVisible }">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg>
            Tools & Platforms
          </h3>

          <div class="tools-grid reveal reveal--d4" :class="{ 'is-visible': isVisible }">
            <div class="tool-item card" v-for="tool in tools" :key="tool">{{ tool }}</div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { watch } from 'vue'
import { useReveal } from '../composables/useReveal'

const { el, isVisible } = useReveal()

watch(isVisible, (val) => {
  if (val) {
    document.querySelectorAll('#resume .reveal').forEach((el, i) => {
      setTimeout(() => el.classList.add('is-visible'), i * 70)
    })
  }
})

const experience = [
  {
    role: 'Data Engineer Intern',
    company: 'Rubixe',
    location: 'Bangalore, India',
    period: '2022',
    color: '#00d4ff',
    bullets: [
      'Designed and delivered Python + Apache Spark ETL pipelines processing millions of records daily from heterogeneous data sources.',
      'Rebuilt data ingestion workflows in collaboration with the data science team, improving throughput and reducing pipeline latency.',
      'Built schema validation and data quality checks that enforced consistency and reliability across all ingestion workflows.',
    ],
    tech: ['Python', 'Spark', 'ETL', 'SQL'],
  },
  {
    role: 'Jr. Software Engineer',
    company: 'Sunflower Lab',
    location: 'Gujarat, India',
    period: '2022 – 2023',
    color: '#8b5cf6',
    bullets: [
      'Designed and maintained RESTful APIs and backend data services powering core product features.',
      'Optimized SQL queries and normalized database schemas across SQL Server and PostgreSQL, cutting average query execution time by 40%.',
      'Integrated third-party data sources into internal systems, improving data availability for downstream analytics teams.',
    ],
    tech: ['SQL Server', 'Python', 'PostgreSQL', 'GitHub'],
  },
  {
    role: 'Product Analyst',
    company: 'Exxat',
    location: 'Gujarat, India',
    period: '2023 – 2024',
    color: '#34d399',
    bullets: [
      'Analyzed product usage datasets and delivered insights that directly shaped roadmap decisions for senior stakeholders.',
      'Built interactive Power BI dashboards tracking 15+ core product KPIs, adopted across engineering, product, and leadership teams.',
      'Modernized SSIS-based reporting pipelines in SQL Server, eliminating manual data handling and reducing report generation time significantly.',
      'Established data contracts with engineering teams that standardized reporting schemas and reduced cross-team integration errors.',
    ],
    tech: ['Power BI', 'SSIS', 'SQL Server', 'Python', 'Tableau'],
  },
]

const keySkills = [
  { name: 'ETL / Data Pipelines', level: 90 },
  { name: 'SQL & Databases', level: 90 },
  { name: 'Python', level: 80 },
  { name: 'Apache Spark', level: 78 },
  { name: 'Power BI / Tableau', level: 75 },
  { name: 'Docker & Kubernetes', level: 67 },
  { name: 'Cloud (Azure / AWS)', level: 65 },
  { name: 'Linux', level: 60 },
]

const tools = [
  'VS Code', 'SSMS', 'Azure Data Studio', 'Databricks',
  'Jupyter Notebook', 'GitHub', 'Jira', 'Salesforce',
  'Power BI Desktop', 'Airflow UI', 'Docker Desktop', 'Postman',
]
</script>

<style scoped>
.resume__inner {
  max-width: 1200px;
  margin: 0 auto;
}

.resume__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  align-items: start;
}

.resume__col-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  font-weight: 700;
  color: var(--color-text);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 14px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--color-border);
}
.resume__col-title svg { color: var(--color-accent); }
.resume__col-title--mt { margin-top: 20px; }

/* Timeline */
.timeline { position: relative; }
.timeline::before {
  content: '';
  position: absolute;
  left: 7px; top: 8px;
  bottom: 0; width: 1px;
  background: linear-gradient(to bottom, var(--color-accent), transparent);
}

.timeline__item {
  position: relative;
  padding-left: 28px;
  margin-bottom: 12px;
}
.timeline__dot {
  position: absolute;
  left: 0; top: 20px;
  width: 14px; height: 14px;
  border-radius: 50%;
  border: 2px solid var(--color-bg-secondary);
  z-index: 1;
  box-shadow: 0 0 8px currentColor;
}

.timeline__card { padding: 12px 16px; }

.timeline__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}
.timeline__role {
  font-size: 13px;
  font-weight: 700;
  color: var(--color-text);
  margin-bottom: 2px;
  letter-spacing: -0.01em;
}
.timeline__company {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--color-text-secondary);
  font-family: var(--font-mono);
}
.timeline__company svg { color: var(--color-accent); flex-shrink: 0; }
.timeline__date { flex-shrink: 0; }

.timeline__bullets {
  padding-left: 16px;
  margin-bottom: 8px;
}
.timeline__bullets li {
  font-size: 11.5px;
  color: var(--color-text-secondary);
  line-height: 1.5;
  margin-bottom: 3px;
  list-style: disc;
}
.timeline__bullets li::marker { color: var(--color-accent); }

.timeline__tech { display: flex; flex-wrap: wrap; gap: 6px; }

/* Skills summary */
.skills-summary { margin-bottom: 8px; }
.skills-summary__item { margin-bottom: 8px; }
.skills-summary__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}
.skills-summary__name {
  font-size: 13px;
  font-weight: 500;
  color: var(--color-text);
}
.skills-summary__pct {
  font-size: 12px;
  font-weight: 700;
  font-family: var(--font-mono);
}
.skills-summary__bar-bg {
  height: 6px;
  background: rgba(255,255,255,0.06);
  border-radius: 3px;
  overflow: hidden;
  position: relative;
}
.skills-summary__bar-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 1.2s cubic-bezier(0.4, 0, 0.2, 1);
  background: linear-gradient(90deg,
    #00d4ff 0%,
    #8b5cf6 35%,
    #00d4ff 65%,
    #8b5cf6 100%
  );
  background-size: 300% 100%;
  animation: living-bar 6s linear infinite;
  position: relative;
  overflow: hidden;
  box-shadow: 0 0 10px rgba(0, 212, 255, 0.35);
}
.skills-summary__bar-fill::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.3) 50%,
    transparent 100%
  );
  animation: shimmer-sweep 4.5s ease-in-out infinite;
  transform: translateX(-100%);
}

@keyframes living-bar {
  0%   { background-position: 300% 50%; }
  100% { background-position: 0% 50%; }
}

@keyframes shimmer-sweep {
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(500%); }
}

/* Tools grid */
.tools-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
.tool-item {
  padding: 10px 14px;
  font-size: 12.5px;
  font-weight: 500;
  font-family: var(--font-mono);
  color: var(--color-text-secondary);
  border-radius: var(--radius-sm);
}

@media (max-width: 900px) {
  .resume__grid { grid-template-columns: 1fr; gap: 32px; }
  .tools-grid { grid-template-columns: repeat(3, 1fr); }
}
@media (max-width: 768px) {
  .resume__grid { gap: 24px; }
  .timeline__card { padding: 16px; }
  .timeline__header { flex-direction: column; align-items: flex-start; gap: 8px; }
  .timeline__role { font-size: 14px; }
  .timeline__bullets li { font-size: 12px; }
  .tools-grid { grid-template-columns: repeat(2, 1fr); }
  .resume__col-title { font-size: 12px; }
}
@media (max-width: 480px) {
  .timeline { margin-left: 0; }
  .timeline__item { padding-left: 22px; }
  .timeline__dot { width: 12px; height: 12px; }
  .timeline__card { padding: 12px 14px; }
  .tools-grid { grid-template-columns: 1fr 1fr; gap: 6px; }
  .tool-item { padding: 8px 10px; font-size: 11.5px; }
  .skills-summary__name { font-size: 12px; }
}
</style>
